/**
 * React.lazy with recovery for the one failure code-splitting introduces:
 * a route chunk that cannot be fetched.
 *
 * Almost always this is a deployment version mismatch — the open tab is on
 * build A, build B replaced the hashed chunk files, and the chunk it asks
 * for now 404s — or a brief network blip. So:
 *   1. retry the import once after a short pause (network blip);
 *   2. if it still fails, reload the page ONCE onto the new build
 *      (guarded in sessionStorage so a genuinely broken chunk can never
 *      reload-loop);
 *   3. otherwise rethrow, and the app's <ErrorBoundary> shows its normal
 *      "reload this section" screen.
 */
import { lazy } from 'react';
import { isChunkLoadError } from '../error-framework/globalErrorHandlers';

const RELOAD_KEY = 'tsg:chunk-reload';
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function lazyWithRetry(factory) {
  return lazy(async () => {
    try {
      const mod = await factory();
      try { window.sessionStorage.removeItem(RELOAD_KEY); } catch (_) { /* private mode */ }
      return mod;
    } catch (firstError) {
      if (!isChunkLoadError(firstError)) throw firstError;
      await wait(800);
      try {
        return await factory();
      } catch (error) {
        let reloaded = false;
        try { reloaded = window.sessionStorage.getItem(RELOAD_KEY) === '1'; } catch (_) { /* private mode */ }
        if (!reloaded) {
          try { window.sessionStorage.setItem(RELOAD_KEY, '1'); } catch (_) { /* private mode */ }
          window.location.reload();
          // Keep React suspended while the page reloads.
          return new Promise(() => {});
        }
        throw error;
      }
    }
  });
}
